import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import { API_URL, APP_VERSION, DATADOG_RUM_APPLICATION_ID, DATADOG_RUM_CLIENT_TOKEN, HOST, MODE } from '~/constants';
import { PlatformModel } from '~/models/PlatformModel';
import { DashboardUser } from '~/repositories';
import { log } from '~/util/log';

export const enum MonitoringAction {
  CheckScannerTestConnection = 'Check Scanner: "Test Connection"',
}

const isRumEnabled = DATADOG_RUM_APPLICATION_ID && DATADOG_RUM_CLIENT_TOKEN && window.location.hostname === HOST;

export const initMonitoring = () => {
  if (!isRumEnabled) {
    return;
  }

  datadogRum.init({
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'dashboard',
    env: MODE,
    version: APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 10,
    defaultPrivacyLevel: 'allow',
    plugins: [reactPlugin({ router: true })],
    allowedTracingUrls: [
      {
        match: API_URL,
        propagatorTypes: ['tracecontext'],
      },
    ],
    beforeSend: (event, context) => {
      // Ignore loading the bundle
      if (event.type === 'resource' && event.resource.url.endsWith('.js')) {
        return false;
      }

      // Ignore loading LaunchDarkly events
      if (event.type === 'resource' && event.resource.url.includes('clienstream.launchdarkly.com')) {
        return false;
      }

      // Include Request-Id header on Column API requests
      if (
        event.type === 'resource' &&
        event.resource.url.includes('api.column.com') &&
        'response' in context &&
        context.response?.headers
      ) {
        const requestId = context.response.headers.get('Request-Id');
        if (requestId) {
          event.context = { ...event.context, requestId };
        }
      }

      return true;
    },
  });
};

export const recordMonitoringAction = (action: MonitoringAction, context?: Record<string, any>) => {
  if (!isRumEnabled) {
    return;
  }
  datadogRum.addAction(action, context);
};

export const startMonitoringSession = (user: DashboardUser, platform?: PlatformModel) => {
  if (!isRumEnabled) {
    return;
  }

  setMonitoringContext({ platform });

  try {
    datadogLogs.setUser({
      id: user.id,
      email: user.email,
    });
  } catch (error) {
    log({
      name: 'startMonitoringSession',
      context: { error },
    });
  }

  try {
    datadogRum.setUser({
      id: user.id,
      email: user.email,
    });
  } catch (error) {
    log({
      name: 'startMonitoringSession',
      context: { error },
    });
  }
};

export const setMonitoringContext = ({ platform }: { platform?: PlatformModel }) => {
  if (!isRumEnabled) {
    return;
  }

  if (!platform) {
    return;
  }

  try {
    datadogLogs.setGlobalContextProperty('platform', { id: platform.id, name: platform.name });
  } catch (error) {
    log({
      name: 'setPlatformContext',
      context: { error },
    });
  }

  try {
    datadogRum.setGlobalContextProperty('platform', { id: platform.id, name: platform.name });
  } catch (error) {
    log({
      name: 'setPlatformContext',
      context: { error },
    });
  }
};

export const stopMonitoringSession = () => {
  if (!isRumEnabled) {
    return;
  }

  try {
    datadogRum.clearUser();
    datadogRum.stopSession();
  } catch (error) {
    log({
      name: 'stopMonitoringSession',
      context: { error },
    });
  }
};
